// src/utils/textFormatting.js

export const formatTextContent = (content) => {
  if (!content) return null;
  
  // Process inline markdown (bold, italic) and return JSX elements
  const processInlineMarkdown = (text) => {
    const elements = [];
    let lastIndex = 0;
    
    // Combined regex for **bold** and *italic* (but not bullet points at line start)
    const regex = /(\*\*([^*]+)\*\*)|(?<!\n\s{0,10})\*([^*\n]+)\*/g;
    let match;
    
    while ((match = regex.exec(text)) !== null) {
      // Add text before match
      if (match.index > lastIndex) {
        elements.push(text.substring(lastIndex, match.index));
      }
      
      if (match[1]) {
        // Bold: **text**
        elements.push(
          <strong key={`b-${match.index}`} style={{fontWeight: '600', color: '#2d3748'}}>
            {match[2]}
          </strong>
        );
      } else if (match[3]) {
        // Italic: *text* (but not bullet point)
        elements.push(
          <em key={`i-${match.index}`} style={{fontStyle: 'italic', color: '#4a5568'}}>
            {match[3]}
          </em>
        );
      }
      
      lastIndex = match.index + match[0].length;
    }
    
    // Add remaining text
    if (lastIndex < text.length) {
      elements.push(text.substring(lastIndex));
    }
    
    return elements.length > 0 ? elements : text;
  };
  
  // Check for bullet points (lines starting with *)
  const bulletPointRegex = /^\s*\*\s+/gm;
  const hasBulletPoints = bulletPointRegex.test(content);
  
  if (hasBulletPoints) {
    const lines = content.split('\n');
    const items = [];
    let currentItem = [];
    let intro = [];
    let inBullets = false;
    
    lines.forEach((line) => {
      const trimmedLine = line.trim();
      
      if (trimmedLine.startsWith('* ')) {
        // Start of bullet point
        if (currentItem.length > 0) {
          // Save previous item
          items.push(currentItem.join(' '));
          currentItem = [];
        }
        inBullets = true;
        // Remove the "* " and add the rest
        currentItem.push(trimmedLine.substring(2));
      } else if (trimmedLine && inBullets) {
        // Continuation of current bullet
        currentItem.push(trimmedLine);
      } else if (trimmedLine && !inBullets) {
        // Text before bullets - intro
        intro.push(trimmedLine);
      }
    });
    
    // Add last item
    if (currentItem.length > 0) {
      items.push(currentItem.join(' '));
    }
    
    return (
      <div>
        {intro.length > 0 && (
          <p className="formatted-paragraph">
            {processInlineMarkdown(intro.join(' '))}
          </p>
        )}
        <ul className="formatted-list" style={{paddingLeft: '24px', marginTop: intro.length > 0 ? '12px' : '0',textAlign: 'left' }}>
          {items.map((item, index) => (
            <li key={index} className="formatted-list-item" style={{
              marginBottom: '10px',
              lineHeight: '1.6',
              color: '#2d3748',
              textAlign: 'left'
            }}>
              {processInlineMarkdown(item)}
            </li>
          ))}
        </ul>
      </div>
    );
  }
  
  // Check for numbered lists (1. 2. 3. or 1) 2) 3))
  const numberedListRegex = /^\s*\d+[.)]\s+/gm;
  const hasNumberedList = numberedListRegex.test(content);
  
  if (hasNumberedList) {
    const lines = content.split('\n');
    const items = [];
    let currentItem = [];
    let intro = [];
    let inList = false;
    
    lines.forEach((line) => {
      const trimmedLine = line.trim();
      const listMatch = trimmedLine.match(/^(\d+)[.)]\s+(.+)$/);
      
      if (listMatch) {
        // Start of numbered item
        if (currentItem.length > 0) {
          items.push(currentItem.join(' '));
          currentItem = [];
        }
        inList = true;
        currentItem.push(listMatch[2]); // Text after number
      } else if (trimmedLine && inList) {
        // Continuation of current item
        currentItem.push(trimmedLine);
      } else if (trimmedLine && !inList) {
        // Intro text
        intro.push(trimmedLine);
      }
    });
    
    // Add last item
    if (currentItem.length > 0) {
      items.push(currentItem.join(' '));
    }
    
    return (
      <div>
        {intro.length > 0 && (
          <p className="formatted-paragraph">
            {processInlineMarkdown(intro.join(' '))}
          </p>
        )}
        <ol className="formatted-list" style={{paddingLeft: '24px', marginTop: intro.length > 0 ? '12px' : '0',textAlign: 'left' }}>
          {items.map((item, index) => (
            <li key={index} className="formatted-list-item" style={{
              marginBottom: '10px',
              lineHeight: '1.6',
              color: '#2d3748',
              textAlign: 'left'
            }}>
              {processInlineMarkdown(item)}
            </li>
          ))}
        </ol>
      </div>
    );
  }
  
  // Check for "Example 1:", "Example 2:" pattern
  const examplePattern = /Example\s+\d+:/gi;
  const hasExamples = examplePattern.test(content);
  
  if (hasExamples) {
    // Split by Example markers but keep the marker
    const parts = content.split(/(?=Example\s+\d+:)/gi);
    
    return (
      <div>
        {parts.map((part, index) => {
          const trimmedPart = part.trim();
          if (!trimmedPart) return null;
          
          // Check if this part starts with "Example"
          const isExample = /^Example\s+\d+:/i.test(trimmedPart);
          
          return (
            <div 
              key={index} 
              className="formatted-paragraph" 
              style={{
                marginBottom: '16px',
                padding: isExample ? '12px' : '0',
                backgroundColor: isExample ? '#f9fafb' : 'transparent',
                borderLeft: isExample ? '4px solid #667eea' : 'none',
                borderRadius: isExample ? '4px' : '0',
                textAlign: 'left'
              }}
            >
              {processInlineMarkdown(trimmedPart)}
            </div>
          );
        })}
      </div>
    );
  }
  
  // Default: paragraphs separated by double newlines
  const paragraphs = content.split(/\n\s*\n/).filter(p => p.trim());
  
  if (paragraphs.length > 1) {
    return (
      <div>
        {paragraphs.map((para, index) => (
          <p key={index} className="formatted-paragraph" style={{marginBottom: '12px', lineHeight: '1.6',textAlign: 'left'}}>
            {processInlineMarkdown(para.trim())}
          </p>
        ))}
      </div>
    );
  }
  
  // Single paragraph
  return (
    <p className="formatted-paragraph" style={{lineHeight: '1.6',textAlign: 'left'}}>
      {processInlineMarkdown(content.trim())}
    </p>
  );
};

